import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  MenuItem,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { Close as CloseIcon, Send as SendIcon, MailOutline as MailIcon } from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import {
  fetchOrganizationDetails,
  generateInvoicePdfBlob,
  getInvoiceById,
  sendInvoiceEmailToTeam,
} from '../../../services/invoiceRecords.service'
import usePermission from '../../../hooks/usePermission'
import ExportZohoDialog from './ExportZohoDialog'
import InvoicePdfPreview from './InvoicePdfPreview'
import EmailConfirmationDialog from './EmailConfirmationDialog'
import {
  fetchPlanEmailRecipients,
  fetchTeamEmailRecipients,
} from '../../../services/invoiceRecords.service'

/**
 * Modal for bulk invoice actions
 * Allows editing invoice date, due date, and status for multiple invoices
 * Also provides bulk email and Zoho export functionality
 */
const InvoiceActionsModal = ({
  open,
  onClose,
  selectedInvoices,
  period,
  onUpdate,
  onCreate,
  onSendEmail,
  onExportToZoho,
  canCreate: propCanCreate,
  canUpdate: propCanUpdate,
  canZohoSend: propCanZohoSend,
}) => {
  const { can } = usePermission()
  const canCreate = propCanCreate !== undefined ? propCanCreate : can('invoice.create')
  const canUpdate = propCanUpdate !== undefined ? propCanUpdate : (can('invoice.update') || can('invoice.paymentstatus.update') || can('invoice.automation.update'))
  const canZohoSend = propCanZohoSend !== undefined ? propCanZohoSend : can('invoice.zoho.send')
  const [invoiceUpdates, setInvoiceUpdates] = useState({})
  const [loading, setLoading] = useState(false)
  const [sendingTeamEmailId, setSendingTeamEmailId] = useState(null)
  const [sendingCustomerEmailId, setSendingCustomerEmailId] = useState(null)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [notification, setNotification] = useState(null)

  // Zoho Export Dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportTargetIds, setExportTargetIds] = useState([])
  const [exportDetails, setExportDetails] = useState({ plantName: '', invoiceNumber: '', count: 1 })
  const [zohoSyncDialogOpen, setZohoSyncDialogOpen] = useState(false)
  const [targetInvoiceId, setTargetInvoiceId] = useState(null)

  // Email Confirmation Dialog State
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [emailType, setEmailType] = useState('customer') // 'customer' or 'team'
  const [emailDialogData, setEmailDialogData] = useState({
    toEmails: [],
    ccEmails: [],
    isLoading: false,
    error: null,
  })
  const [isSendingEmail, setIsSendingEmail] = useState(false)

  // State for hidden invoice preview rendering
  const [invoiceForPreview, setInvoiceForPreview] = useState(null)
  const [orgDataForPreview, setOrgDataForPreview] = useState(null)

  const statusOptions = ['Unpaid', 'Paid', 'Pending']

  //date helpers
  const today = dayjs()
  // Use period for validation range, default to current month if period is missing or invalid
  const validationReference = period && dayjs(period).isValid() ? dayjs(period) : today
  const startOfMonth = validationReference.startOf('month')
  const endOfMonth = validationReference.endOf('month')

  // Helper to normalize status for display (e.g., "PAID" -> "Paid", "UNPAID" -> "Unpaid")
  const normalizeStatusForDisplay = (status) => {
    if (!status) return 'Unpaid'
    const lowerStatus = status.toLowerCase().trim()
    // Map any variations to our standard options
    if (lowerStatus === 'paid') return 'Paid'
    if (lowerStatus === 'unpaid') return 'Unpaid'
    if (lowerStatus === 'pending') return 'Pending'
    if (lowerStatus === 'to be raised') return 'To Be Raised'
    // For any other status, capitalize first letter
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  }

  useEffect(() => {
    if (open && selectedInvoices.length > 0) {
      // Initialize update state for each invoice
      const updates = {}
      selectedInvoices.forEach((invoice) => {
        // Use paymentStatus first (from DB), then fall back to status field
        let currentStatus = invoice.paymentStatus || invoice.status || 'unpaid'

        // Default billing records to 'To Be Raised'
        if (invoice.recordType === 'BILLING') {
          currentStatus = 'To Be Raised'
        }

        const displayStatus = normalizeStatusForDisplay(currentStatus)
        console.log(`Invoice ${invoice._id} - paymentStatus: ${invoice.paymentStatus}, status: ${invoice.status}, displayStatus: ${displayStatus}`)
        updates[invoice._id] = {
          invoiceDate: invoice.invoiceDate
            ? dayjs(invoice.invoiceDate)
            : (period && dayjs(period).isValid() ? dayjs(period).startOf('month') : dayjs()),
          dueDate: dayjs(invoice.dueDate),
          status: displayStatus,
          automated: invoice.automated || false,
        }
      })
      setInvoiceUpdates(updates)
      setNotification(null)
    }
  }, [open, selectedInvoices])

  // Check if all invoices meet criteria for bulk actions
  const canExportAllToZoho = () => {
    return selectedInvoices.every((invoice) => invoice.sentToZoho === false)
  }

  const canSendAllCustomerEmails = () => {
    return selectedInvoices.every((invoice) => invoice.emailSent === false)
  }

  const allCanExportToZoho = canExportAllToZoho()
  const allCanSendEmail = canSendAllCustomerEmails()

  const handleFieldChange = (invoiceId, field, value) => {
    setInvoiceUpdates((prev) => ({
      ...prev,
      [invoiceId]: {
        ...prev[invoiceId],
        [field]: value,
      },
    }))
  }

  const handleUpdateInvoice = async (invoiceId) => {
    setLoading(true)
    try {
      const updates = invoiceUpdates[invoiceId]
      const formattedUpdates = {
        invoiceDate: updates.invoiceDate.toISOString(),
        dueDate: updates.dueDate.toISOString(),
        status: updates.status.toUpperCase(),
        automated: updates.automated,
      }

      const success = await onUpdate(invoiceId, formattedUpdates)
      if (success) {
        setNotification({
          type: 'success',
          message: `Invoice updated successfully`,
        })
      }
    } catch (error) {
      console.error('Error updating invoice:', error)
      setNotification({
        type: 'error',
        message: 'Failed to update invoice',
      })
    } finally {
      setLoading(false)
    }
  }



  const [conversionDialog, setConversionDialog] = useState({
    open: false,
    invoiceId: null,
    status: '',
    invoiceNumber: ''
  })

  const handleSaveConversion = (invoiceId) => {
    const updates = invoiceUpdates[invoiceId]
    const invoice = selectedInvoices.find((inv) => inv._id === invoiceId)

    setConversionDialog({
      open: true,
      invoiceId,
      status: updates.status,
      plantName: invoice.plantName,
    })
  }

  const handleConfirmConversion = async () => {
    const { invoiceId, status } = conversionDialog
    setConversionDialog({ ...conversionDialog, open: false })

    // Original logic
    const invoice = selectedInvoices.find((inv) => inv._id === invoiceId)
    const updates = invoiceUpdates[invoiceId]

    setLoading(true)
    try {
      const formattedData = {
        ...invoice,
        invoiceDate: updates.invoiceDate.toISOString(),
        dueDate: updates.dueDate.toISOString(),
        paymentStatus: 'To Be Exported', // Enforce "To Be Exported" for new invoices from Billing Records
        billingRecordId: invoice._id,
      }

      const success = await onCreate(formattedData)
      if (success) {
        // If conversion is successful, we should probably close the modal as the list will refresh
        // and this item will transform into an invoice or disappear from "To Be Raised"
        onClose()
      }
    } catch (error) {
      console.error('Error converting billing record:', error)
      setNotification({
        type: 'error',
        message: 'Failed to generate invoice',
      })
    } finally {
      setLoading(false)
    }
  }


  const handleSendAllCustomerEmails = async () => {
    if (!allCanSendEmail) {
      const alreadySentCount = selectedInvoices.filter(
        (inv) => inv.emailSent === true
      ).length
      setNotification({
        type: 'warning',
        message: `Cannot send emails: ${alreadySentCount} invoice(s) already have emails sent.`,
      })
      return
    }

    try {
      setIsBulkProcessing(true)
      const invoiceIds = selectedInvoices.map((inv) => inv._id)
      console.log(`Starting bulk email process for ${invoiceIds.length} invoices...`)

      for (const inv of selectedInvoices) {
        await handleSendManualEmail(inv._id, 'customer')
      }

      setNotification({
        type: 'success',
        message: `Bulk email process completed for ${selectedInvoices.length} invoices.`,
      })
    } catch (error) {
      console.error('Error in bulk email process:', error)
      setNotification({
        type: 'error',
        message: 'Bulk email process encountered an error.',
      })
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleOpenExportDialog = (ids, details = null) => {
    setExportTargetIds(ids)
    if (details) {
      setExportDetails(details)
    } else if (ids.length === 1) {
      const inv = selectedInvoices.find(i => i._id === ids[0])
      setExportDetails({
        plantName: inv?.plantName || '',
        invoiceNumber: inv?.invoiceNumber || '',
        count: 1
      })
    } else {
      setExportDetails({ count: ids.length })
    }
    setExportDialogOpen(true)
  }

  const handleConfirmExport = async () => {
    setExportDialogOpen(false)
    if (onExportToZoho) {
      setLoading(true) // Start loading (spinner will show on action buttons if wired, or global loading)
      // Since individual button has its own loading indicator logic based on 'loading' state, this works
      try {
        await onExportToZoho(exportTargetIds)
        // Update local state to reflect change immediately for better UX
        // The parent 'loadInvoices' usually refreshes, but we can optimistically update or rely on parent
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleZohoExportAll = () => {
    if (!allCanExportToZoho) {
      const alreadySentCount = selectedInvoices.filter(
        (inv) => inv.sentToZoho === true
      ).length
      setNotification({
        type: 'warning',
        message: `Cannot export to Zoho: ${alreadySentCount} invoice(s) already exported.`,
      })
      return
    }

    handleOpenExportDialog(selectedInvoices.map(inv => inv._id), { count: selectedInvoices.length })
  }

  const handleSendManualEmail = async (invoiceId, recipientType = 'customer') => {
    setTargetInvoiceId(invoiceId)
    setEmailType(recipientType)
    setEmailDialogOpen(true)
    setEmailDialogData((prev) => ({ ...prev, isLoading: true, error: null, toEmails: [], ccEmails: [] }))

    try {
      let data;
      if (recipientType === 'customer') {
        const invoice = selectedInvoices.find(inv => inv._id === invoiceId)
        if (invoice && invoice.plantId) {
          data = await fetchPlanEmailRecipients(invoice.plantId)
        } else {
          // Fallback in case invoice unavailable in selection, fetch by ID
          const invData = await getInvoiceById(invoiceId)
          if (invData.success && invData.data && invData.data.plantId) {
            data = await fetchPlanEmailRecipients(invData.data.plantId)
          }
        }
      } else {
        data = await fetchTeamEmailRecipients()
      }

      setEmailDialogData({
        toEmails: data?.to || [],
        ccEmails: data?.cc || [],
        isLoading: false,
        error: null,
      })
    } catch (error) {
      console.error("Error fetching email recipients:", error);
      setEmailDialogData(prev => ({ ...prev, isLoading: false, error: 'Failed to fetch recipients' }))
    }
  }

  const handleConfirmSendEmail = async () => {
    if (!targetInvoiceId) return
    setIsSendingEmail(true)
    try {
      await performSendEmail(targetInvoiceId, emailType)
      // Dialog closes in performSendEmail success path or manually here if needed
      // but performSendEmail sets loading states on specific IDs.
      // We should close dialog after success.
      setEmailDialogOpen(false)
    } catch (err) {
      console.error("Email send failed", err)
    } finally {
      setIsSendingEmail(false)
    }
  }

  const handleSyncToZohoClick = (invoiceId) => {
    setTargetInvoiceId(invoiceId);
    setZohoSyncDialogOpen(true);
  }

  const handleConfirmSyncToZoho = () => {
    setZohoSyncDialogOpen(false);
    handleOpenExportDialog([targetInvoiceId])
  }


  const performSendEmail = async (invoiceId, recipientType = 'customer') => {
    if (recipientType === 'customer') {
      setSendingCustomerEmailId(invoiceId)
    } else {
      setSendingTeamEmailId(invoiceId)
    }
    setNotification(null)

    try {
      // Load invoice data and organization details for hidden preview
      console.log(`Loading invoice ${invoiceId} for PDF generation (${recipientType})...`)
      const invoiceResponse = await getInvoiceById(invoiceId)

      if (!invoiceResponse.success || !invoiceResponse.data) {
        throw new Error('Failed to load invoice details')
      }

      const invoice = invoiceResponse.data
      let orgData = null

      if (invoice.organizationId) {
        orgData = await fetchOrganizationDetails(invoice.organizationId)
      }

      // Set state to render hidden preview
      setInvoiceForPreview(invoice)
      setOrgDataForPreview(orgData)

      // Wait for component to fully render with all data (images, formatting, etc.)
      await new Promise(resolve => setTimeout(resolve, 600))

      // Now try to find the preview element
      const previewElement = document.getElementById('invoice-pdf-preview-for-email')
      if (!previewElement) {
        throw new Error('Failed to render invoice preview for PDF generation')
      }

      let result
      if (recipientType === 'customer') {
        // Generate the blob here to pass it to the hook
        const pdfBlob = await generateInvoicePdfBlob(previewElement, true)
        const success = await onSendEmail([invoiceId], 'customer', pdfBlob)
        result = { success, message: success ? 'Email sent successfully to customer' : 'Failed to send email to customer' }
      } else {
        result = await sendInvoiceEmailToTeam(invoiceId, 'team', previewElement)
      }

      if (result.success) {
        setNotification({
          type: 'success',
          message: result.message || `Email sent successfully to ${recipientType}`,
        })
      } else {
        setNotification({
          type: 'error',
          message: result.message || `Failed to send email to ${recipientType}`,
        })
      }
    } catch (error) {
      console.error(`Error sending email to ${recipientType}:`, error)
      setNotification({
        type: 'error',
        message: error.message || `Failed to send email to ${recipientType}`,
      })
    } finally {
      setSendingTeamEmailId(null)
      setSendingCustomerEmailId(null)
      // Clean up preview state
      setInvoiceForPreview(null)
      setOrgDataForPreview(null)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Invoice Actions ({selectedInvoices.length} selected)
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent>
        {notification && (
          <Alert
            severity={notification.type}
            onClose={() => setNotification(null)}
            sx={{ mb: 2 }}
          >
            {notification.message}
          </Alert>
        )}

        {/* Bulk Action Buttons - Only show if more than 1 invoice selected */}
        {selectedInvoices.length > 1 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!allCanSendEmail || loading || isBulkProcessing}
              startIcon={<SendIcon />}
              onClick={handleSendAllCustomerEmails}
              fullWidth
              sx={{ flex: 1, minWidth: '200px' }}
            >
              {isBulkProcessing ? <CircularProgress size={24} color="inherit" /> : 'Send All Customer Emails'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={!allCanExportToZoho || loading || !canZohoSend}
              onClick={handleZohoExportAll}
              fullWidth
              sx={{ flex: 1, minWidth: '200px' }}
            >
              Zoho Export All ({selectedInvoices.length})
            </Button>
          </Box>
        )}

        {!allCanSendEmail && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Note: Some invoices already have emails sent
          </Typography>
        )}

        {!allCanExportToZoho && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Note: Some invoices already exported to Zoho
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Individual Invoice Actions */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={3}>
            {selectedInvoices.map((invoice) => {
              const isBilling = invoice.recordType === 'BILLING'
              return (
                <Box
                  key={invoice._id}
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {/* Plant Name Header */}
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {invoice.plantName}
                    <Chip
                      label={invoice.invoiceNumber}
                      size="small"
                      sx={{ ml: 1 }}
                      variant="outlined"
                    />
                  </Typography>

                  {/* Edit Fields */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    {can('invoice.create') && (
                      <DatePicker
                        label="Invoice Date"
                        value={invoiceUpdates[invoice._id]?.invoiceDate || today}
                        minDate={startOfMonth}
                        maxDate={endOfMonth}
                        onChange={(newValue) =>
                          handleFieldChange(invoice._id, 'invoiceDate', newValue)
                        }

                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { flex: 1, minWidth: '150px' },
                            inputProps: { readOnly: true },
                          },
                        }}
                      />
                    )}

                    {can('invoice.create') && (
                      <DatePicker
                        label="Due Date"
                        value={invoiceUpdates[invoice._id]?.dueDate || dayjs()}
                        onChange={(newValue) =>
                          handleFieldChange(invoice._id, 'dueDate', newValue)
                        }
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { flex: 1, minWidth: '150px' },
                          },
                        }}
                      />
                    )}

                    {(can('invoice.create') || can('invoice.paymentstatus.update')) && (
                      <TextField
                        select
                        label="Status"
                        size="small"
                        value={isBilling ? 'To Be Raised' : (invoiceUpdates[invoice._id]?.status || 'Unpaid')}
                        onChange={(e) =>
                          handleFieldChange(invoice._id, 'status', e.target.value)
                        }
                        sx={{ flex: 1, minWidth: '150px' }}
                        disabled={isBilling}
                      >
                        {/* Ensure 'To Be Raised' is available for rendering when locked */}
                        {isBilling && (
                          <MenuItem value="To Be Raised">
                            To Be Raised
                          </MenuItem>
                        )}
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Box>

                  {/* Payment Reminder Toggle */}
                  {(can('invoice.create') || can('invoice.automation.update')) && (
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={invoiceUpdates[invoice._id]?.automated || false}
                            onChange={(e) =>
                              handleFieldChange(invoice._id, 'automated', e.target.checked)
                            }
                            color="primary"
                            disabled={isBilling}
                          />
                        }
                        label="Set Payment Reminder"
                      />
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mt: 2 }}>

                    {/* Left Group: Emails & Sync */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {can('invoice.create') && (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isBilling || loading || sendingTeamEmailId === invoice._id || sendingCustomerEmailId === invoice._id}
                          onClick={() => handleSendManualEmail(invoice._id, 'team')}
                          startIcon={sendingTeamEmailId === invoice._id ? <CircularProgress size={16} /> : <MailIcon />}
                        >
                          {sendingTeamEmailId === invoice._id ? 'Sending...' : 'Send Email to Team'}
                        </Button>
                      )}

                      {can('invoice.create') && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          disabled={isBilling || loading || invoice.emailSent === true || sendingCustomerEmailId === invoice._id || sendingTeamEmailId === invoice._id}
                          onClick={() => handleSendManualEmail(invoice._id, 'customer')}
                          startIcon={sendingCustomerEmailId === invoice._id ? <CircularProgress size={16} /> : <SendIcon />}
                        >
                          {sendingCustomerEmailId === invoice._id ? 'Sending...' : (invoice.emailSent ? 'Email Sent' : 'Send Email to Customer')}
                        </Button>
                      )}

                      {canCreate && canZohoSend && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          disabled={isBilling || loading || invoice.sentToZoho === true}
                          onClick={() => handleSyncToZohoClick(invoice._id)}
                        >
                          {invoice.sentToZoho ? 'Zoho Synced' : 'Sync to Zoho'}
                        </Button>
                      )}
                    </Box>

                    {/* Right Group: Update/Save */}
                    <Box>
                      {isBilling ? (
                        can('invoice.create') && (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={loading}
                            onClick={() => handleSaveConversion(invoice._id)}
                          >
                            {loading ? <CircularProgress size={20} /> : 'Raise Invoice'}
                          </Button>
                        )
                      ) : (
                        (can('invoice.create') || can('invoice.paymentstatus.update') || can('invoice.automation.update')) && (
                          <Button
                            variant="contained"
                            size="small"
                            disabled={loading}
                            onClick={() => handleUpdateInvoice(invoice._id)}
                          >
                            {loading ? <CircularProgress size={20} /> : 'Update'}
                          </Button>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
      </DialogActions>

      {/* Hidden invoice preview for PDF generation - positioned off-screen but still renderable */}
      {
        invoiceForPreview && (
          <Box
            sx={{
              position: 'absolute',
              left: '-10000px',
              top: 0,
              width: '800px',
              opacity: 0,
              pointerEvents: 'none',
              zIndex: -1,
            }}
          >
            <InvoicePdfPreview
              invoice={invoiceForPreview}
              organizationData={orgDataForPreview}
              onClose={() => { }}
              id="invoice-pdf-preview-for-email"
            />
          </Box>
        )
      }
      {/* Zoho Export Confirmation */}
      <ExportZohoDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onConfirm={handleConfirmExport}
        plantName={exportDetails.plantName}
        invoiceNumber={exportDetails.invoiceNumber}
        count={exportDetails.count}
      />

      {/* Invoice Conversion Confirmation */}
      <Dialog
        open={conversionDialog.open}
        onClose={() => setConversionDialog({ ...conversionDialog, open: false })}
      >
        <DialogTitle>Confirm Invoice Generation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to generate an invoice for <b>{conversionDialog.plantName}</b> with status <b>{conversionDialog.status}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConversionDialog({ ...conversionDialog, open: false })}>Cancel</Button>
          <Button onClick={handleConfirmConversion} variant="contained" color="primary">Generate</Button>
        </DialogActions>
      </Dialog>

      {/* Email Confirmation Dialog */}
      <EmailConfirmationDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        emailType={emailType}
        onConfirm={handleConfirmSendEmail}
        toEmails={emailDialogData.toEmails}
        ccEmails={emailDialogData.ccEmails}
        isLoading={emailDialogData.isLoading}
        error={emailDialogData.error}
        isSending={isSendingEmail}
      />

      {/* Zoho Sync Confirmation Dialog */}
      <Dialog
        open={zohoSyncDialogOpen}
        onClose={() => setZohoSyncDialogOpen(false)}
      >
        <DialogTitle>Confirm Zoho Sync</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to sync this invoice to Zoho? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZohoSyncDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSyncToZoho} variant="contained" color="secondary">Confirm Sync</Button>
        </DialogActions>
      </Dialog>
    </Dialog >
  )
}

export default InvoiceActionsModal
